<script setup>
import { computed, onMounted, ref } from 'vue'
import { Config } from '@/Config'
import * as Server from '@/services/Server'
import * as Auth from '@/services/Auth'
import PageHeader from '@/components/PageHeader.vue'
import EventList from '@/components/EventList.vue'
import SeasonEventCard from '@/components/SeasonEventCard.vue'

const events = ref([])
const loading = ref(true)
const error = ref(false)

/** Finished events fetched by each click on "show more". */
const FINISHED_PAGE_SIZE = 20

// The archive: the first page arrives with the list, the following ones are appended by the button.
const moreFinished = ref([])
const finishedTotal = ref(0)
const loadingMore = ref(false)
const moreError = ref(false)

const isAuthenticated = Auth.isAuthenticated()

/**
 * The page shows two blocks: what is still open and what is over.
 *
 * "Open" is everything that is not finished, so the scheduled events sit with the running ones
 * (they are the ones to wait for) and keep their own "scheduled" chip. Order inside each block is
 * the one from the API (EventLib::list already sorts running, then scheduled, then finished).
 */
const openEvents = computed(() => events.value.filter(event => event.state !== 'finished'))

/**
 * The open season takes the wide card on the left, with its general standings; the other events —
 * the one-offs running next to it — stack on the right.
 */
const seasonEvent = computed(() => openEvents.value.find(event => event.season) ?? null)
const otherOpenEvents = computed(() => openEvents.value.filter(event => event !== seasonEvent.value))

const finishedEvents = computed(() => [
  ...events.value.filter(event => event.state === 'finished'),
  ...moreFinished.value,
])

/** The button stays until the archive is exhausted (`finishedTotal` comes from the API). */
const hasMoreFinished = computed(() => finishedEvents.value.length < finishedTotal.value)

/**
 * Appends the next chunk of the archive. The offset is how many finished events are already shown:
 * the first page is shorter than the following ones (see EventLib::FINISHED_LIMIT), so a page number
 * would not line up with what is on screen.
 */
const loadMoreFinished = () => {
  if (loadingMore.value) {
    return
  }

  loadingMore.value = true
  moreError.value = false

  Server.get(Config.server.routes.api.events_finished(finishedEvents.value.length, FINISHED_PAGE_SIZE))
    .then(response => {
      moreFinished.value = [...moreFinished.value, ...(response.data?.data ?? [])]
      finishedTotal.value = response.data?.meta?.total ?? finishedTotal.value
    })
    .catch(() => {
      moreError.value = true
    })
    .finally(() => {
      loadingMore.value = false
    })
}

onMounted(() => {
  Server.get(Config.server.routes.api.events_summary)
    .then(response => {
      events.value = response.data?.data ?? []
      finishedTotal.value = response.data?.meta?.finishedTotal ?? 0
    })
    .catch(() => {
      error.value = true
    })
    .finally(() => {
      loading.value = false
    })
})
</script>

<template>
  <main>
    <v-container>
      <PageHeader
        :title="$t('events.title')"
        :subtitle="$t('events.subtitle')"
        icon="mdi-calendar-star"
      />

      <v-alert v-if="error" type="error" variant="tonal" class="mb-4">
        {{ $t('events.loadError') }}
      </v-alert>

      <div v-if="loading" class="d-flex justify-center py-12">
        <v-progress-circular indeterminate color="primary" />
      </div>

      <v-alert v-else-if="!error && !events.length" type="info" variant="tonal">
        {{ $t('events.empty') }}
      </v-alert>

      <template v-else>
        <section v-if="openEvents.length" id="events-open">
          <div class="d-flex align-center ga-2 mb-3">
            <h2 class="text-h6 font-weight-bold mb-0">{{ $t('events.openTitle') }}</h2>
            <v-chip color="green" size="small" variant="tonal" label>{{ openEvents.length }}</v-chip>
          </div>
          <v-row v-if="seasonEvent">
            <v-col cols="12" :lg="otherOpenEvents.length ? 8 : 12">
              <SeasonEventCard :event="seasonEvent" />
            </v-col>

            <v-col v-if="otherOpenEvents.length" cols="12" lg="4">
              <EventList :events="otherOpenEvents" :cols="12" :md="12" :lg="12" />
            </v-col>
          </v-row>

          <EventList v-else :events="openEvents" />
        </section>

        <v-divider v-if="openEvents.length && finishedEvents.length" class="my-8" />

        <section v-if="finishedEvents.length" id="events-finished">
          <div class="d-flex align-center ga-2 mb-3">
            <h2 class="text-h6 font-weight-bold mb-0">{{ $t('events.finishedTitle') }}</h2>
            <v-chip color="grey" size="small" variant="tonal" label>{{ finishedEvents.length }}</v-chip>
          </div>
          <EventList :events="finishedEvents" />

          <!-- The archive is longer than the first page: the rest arrives on demand -->
          <div v-if="hasMoreFinished" class="d-flex justify-center mt-4">
            <v-btn
              variant="tonal"
              prepend-icon="mdi-history"
              :loading="loadingMore"
              @click="loadMoreFinished"
            >
              {{ $t('events.loadMore', { count: FINISHED_PAGE_SIZE }) }}
            </v-btn>
          </div>

          <v-alert v-if="moreError" type="error" variant="tonal" class="mt-4">
            {{ $t('events.loadError') }}
          </v-alert>
        </section>
      </template>

      <p
        v-if="!isAuthenticated && !loading && events.length"
        class="text-caption text-medium-emphasis text-center mt-4"
      >
        {{ $t('events.loginHint') }}
      </p>
    </v-container>
  </main>
</template>
